/**
 * Settings - connection status and configuration.
 *
 * Real from phase 1, because the single most common way this application fails
 * is a missing Companies House key or an unreachable database, and the user
 * needs to be able to see that without reading a terminal.
 *
 * This page reads; it does not write. Everything shown comes from
 * apps/api/.env via the health endpoint. The one setting that is editable from
 * the browser - the Companies House key - has its own page at /api-keys, which
 * writes back to that same file. A secret is never sent to the browser either
 * way: the key appears here only as present/absent.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { healthApi, sicApi } from '../lib/api.js';
import { IconAlert, IconCheck, IconInfo, IconKey } from '../components/Icons.jsx';

/** Renders a boolean as a labelled badge with an explanation attached. */
function StatusBadge({ ok, okLabel, offLabel, tip }) {
  return (
    <span className={`badge ${ok ? 'badge-ok' : 'badge-warn'} tip tip-left`} data-tip={tip}>
      <span className="badge-dot" />
      {ok ? okLabel : offLabel}
    </span>
  );
}

export default function SettingsPage() {
  const [health, setHealth] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const [reloading, setReloading] = useState(false);
  const [reloadResult, setReloadResult] = useState(null);
  const [reloadError, setReloadError] = useState(null);

  const reloadCatalogue = async () => {
    setReloading(true);
    setReloadError(null);
    try {
      setReloadResult(await sicApi.reloadCatalogue());
    } catch (caught) {
      setReloadError(caught.message);
    } finally {
      setReloading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    healthApi
      .check()
      .then((data) => {
        if (!cancelled) setHealth(data);
      })
      .catch((caught) => {
        if (!cancelled) setError(caught.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="page">
        <div className="loading-panel">
          <span className="spinner spinner-lg" />
          Checking connections
        </div>
      </div>
    );
  }

  const db = health?.checks?.database;
  const ch = health?.checks?.companiesHouse;
  const smtp = health?.checks?.smtpProbe;

  return (
    <div className="page">
      <div className="page-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">Settings</h1>
          <p className="page-description">
            Most of what is below is read from <code>apps/api/.env</code> on the server. Edit
            that file and restart the API to change it. Two exceptions: the Companies House API
            key, which you can enter and test on the{' '}
            <Link to="/api-keys">API keys</Link> page, and the SIC catalogue's aliases and
            categories, which reload on demand below.
          </p>
        </div>
        <div className="page-header-actions">
          <Link className="btn" to="/api-keys">
            <IconKey size={14} />
            Manage API keys
          </Link>
        </div>
      </div>

      {error && (
        <div className="alert alert-error" style={{ marginBottom: 'var(--sp-5)' }}>
          <span className="alert-icon">
            <IconAlert />
          </span>
          <span className="alert-body">
            <span className="alert-title">Could not reach the API</span>
            {error}
          </span>
        </div>
      )}

      {/* 2-column tile grid — top row: API + Database; bottom rows: remaining tiles */}
      <div className="settings-tile-grid">
        {/* --- API --- */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">API</span>
            <div className="card-header-actions">
              <span className="badge badge-accent">{health?.phase ?? 'unknown'}</span>
            </div>
          </div>
          <div className="card-body">
            <div className="kv">
              <span className="kv-key">Version</span>
              <span className="kv-value mono">{health?.version ?? '—'}</span>

              <span className="kv-key">Environment</span>
              <span className="kv-value mono">{health?.environment ?? '—'}</span>

              <span className="kv-key">Uptime</span>
              <span className="kv-value mono">
                {health ? `${Math.floor(health.uptimeSeconds / 60)}m ${health.uptimeSeconds % 60}s` : '—'}
              </span>
            </div>
          </div>
        </div>

        {/* --- Database --- */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">Database</span>
            <div className="card-header-actions">
              <StatusBadge
                ok={Boolean(db?.connected)}
                okLabel="Connected"
                offLabel="Unreachable"
                tip={
                  db?.connected
                    ? 'MySQL responded to a SELECT 1 probe.'
                    : 'Check that the MySQL service is running and that DATABASE_URL in apps/api/.env is correct.'
                }
              />
            </div>
          </div>
          <div className="card-body">
            <div className="kv">
              <span className="kv-key">Engine</span>
              <span className="kv-value mono">MySQL 8 · schema leads_scanner</span>

              <span className="kv-key">Round trip</span>
              <span className="kv-value mono">
                {db?.latencyMs != null ? `${db.latencyMs} ms` : <span className="value-absent">not measured</span>}
              </span>

              {db?.error && (
                <>
                  <span className="kv-key">Error</span>
                  <span className="kv-value text-error">{db.error}</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* --- SIC catalogue --- */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">SIC catalogue</span>
          </div>
          <div className="card-body">
            <p className="text-sm text-dim">
              Plain-English aliases and curated categories are read from{' '}
              <code>data/sic-aliases.json</code> and <code>data/sic-categories.json</code> once,
              at startup - an edit to either file has no effect until it is reloaded. This does
              that without restarting the API.
            </p>
            <div className="row row-tight" style={{ marginTop: 'var(--sp-3)' }}>
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => void reloadCatalogue()}
                disabled={reloading}
              >
                {reloading ? <span className="spinner" /> : null}
                Reload catalogue
              </button>
              {reloadResult ? (
                <span className="text-sm text-ok">
                  Reloaded - {reloadResult.codes.toLocaleString()} codes,{' '}
                  {reloadResult.aliases.toLocaleString()} aliases,{' '}
                  {reloadResult.categories.toLocaleString()} categories.
                </span>
              ) : null}
              {reloadError ? <span className="text-sm text-error">{reloadError}</span> : null}
            </div>
          </div>
        </div>

        {/* --- Companies House --- */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">Companies House</span>
            <div className="card-header-actions">
              <StatusBadge
                ok={Boolean(ch?.configured)}
                okLabel="Ready"
                offLabel="Not configured"
                tip={
                  ch?.configured
                    ? 'A key is present and the integration is enabled. Acquisition can run.'
                    : 'Enter a key on the API keys page, or set COMPANIES_HOUSE_API_KEY and COMPANIES_HOUSE_ENABLED=true in apps/api/.env by hand.'
                }
              />
            </div>
          </div>
          <div className="card-body">
            <div className="kv">
              <span className="kv-key">
                API key
                <button
                  type="button"
                  className="info-tip tip tip-below"
                  data-tip="Register a free key at developer.company-information.service.gov.uk, then enter it on the API keys page. The key is stored server-side only and never sent back to the browser."
                  aria-label="Where to get an API key"
                >
                  i
                </button>
              </span>
              <span className="kv-value">
                {ch?.hasKey ? (
                  <span className="text-ok row row-tight">
                    <IconCheck size={13} /> Present
                  </span>
                ) : (
                  <span className="value-absent">not set</span>
                )}
              </span>

              <span className="kv-key">Integration</span>
              <span className="kv-value">
                {ch?.enabled ? 'Enabled' : <span className="value-absent">disabled</span>}
              </span>

              <span className="kv-key">
                Rate limit
                <button
                  type="button"
                  className="info-tip tip tip-below"
                  data-tip="600 requests per 5 minutes per key, for the whole application. The acquisition engine throttles globally to roughly 350ms between calls to stay inside it."
                  aria-label="What the rate limit means"
                >
                  i
                </button>
              </span>
              <span className="kv-value mono">600 requests / 5 minutes</span>
            </div>

            {!ch?.configured && (
              <div className="alert alert-info" style={{ marginTop: 'var(--sp-4)' }}>
                <span className="alert-icon">
                  <IconInfo />
                </span>
                <span className="alert-body">
                  <span className="alert-title">Not needed yet</span>
                  A key is first required in phase 2B, for company-name reverse lookup. Phases 1
                  and 2 run entirely on local seed data. You can add and test one now on the{' '}
                  <Link to="/api-keys">API keys</Link> page.
                </span>
              </div>
            )}
          </div>
        </div>

        {/* --- SMTP probing — spans both columns so it sits full-width at the bottom --- */}
        <div className="card settings-tile-full">
          <div className="card-header">
            <span className="card-title">Email verification</span>
            <div className="card-header-actions">
              <span className={`badge ${smtp?.enabled ? 'badge-ok' : 'badge'}`}>
                <span className="badge-dot" />
                {smtp?.enabled ? 'Probing enabled' : 'Probing off'}
              </span>
            </div>
          </div>
          <div className="card-body">
            <p className="text-sm text-dim">
              SMTP RCPT-TO probing is off by default and arrives in phase 10. Most networks block
              outbound port 25, and a blocked probe proves nothing — so with probing disabled every
              guessed address will read{' '}
              <span
                className="tip"
                data-tip="'blocked' means we could not test the address, not that the address is wrong. It is never presented as evidence against an address."
              >
                blocked
              </span>{' '}
              rather than carrying a fabricated confidence score.
            </p>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
